import React from 'react'

import { At as PositionedText, Block, Sheet } from './_shared/positioned'

// Réplica do modelo "Photographic Report" da Biofix (A4): uma capa com os dados do tratamento e, depois, páginas de
// fotos (2 colunas x 3 linhas), com as coordenadas em pt do modelo. Só a tabela de exportadores e a quantidade de
// páginas de fotos crescem conforme os dados.
const FONT = "Calibri, Carlito, 'Segoe UI', Arial, sans-serif"
const SIZE = 10
const PAGE_WIDTH = 595.5
const PAGE_HEIGHT = 841
// as coordenadas do modelo (PDF original) ficam 7.8pt acima do que aparece na folha
const MODEL_SHIFT = 7.8
const NAVY = '#00063b'
const GREEN = '#4dbd87'
const FOOTER_BACKGROUND = '#23394f'
const FOOTER_TEXT = '#dbe1d8'
const LINE = '0.75pt solid #000'
const ROW_HEIGHT = 23.4
const COMPACT_ROW_HEIGHT = 15
const COMPACT_FROM = 7
// as linhas de texto do navegador ficam cerca de 1pt abaixo das do modelo
const TEXT_OFFSET = 0.9
// a primeira linha do rodapé fica um pouco à esquerda do centro no modelo
const TITLE_OFFSET = 6.8
const KEEP_COLORS = { WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }

const At = ({ size = SIZE, ...props }) => <PositionedText size={size} {...props} />

const Logo = ({ x, y, width, height }) => (
  <img
    src="/pdfs/biofix/logo.png"
    alt="Biofix Agri"
    style={{ position: 'absolute', left: `${x}pt`, top: `${y}pt`, width: `${width}pt`, height: `${height}pt` }}
  />
)

// Rodapé azul-escuro; na capa as linhas de endereço ficam um pouco mais abaixo, como no modelo
const Footer = ({ cover }) => {
  const [first, second, third] = cover ? [790.8, 810.7, 823.5] : [790.8, 805.3, 818.1]
  return (
    <>
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: `${794 - MODEL_SHIFT}pt`,
          width: `${PAGE_WIDTH}pt`,
          height: '46.5pt',
          background: FOOTER_BACKGROUND,
          ...KEEP_COLORS
        }}
      />
      {[
        [first, 11.7, 0.05, 'Biofix Agri LTDA'],
        [second, SIZE, 0.31, 'Avenida Afonso Pena 511 Sala 74/75 - Estuário - Santos/SP'],
        [third, SIZE, 0.1, 'T: +55 13 3877-8322 | E: brazil@biofixagri.com']
      ].map(([y, size, spacing, text]) => (
        <At
          key={text}
          x={size === 11.7 ? -TITLE_OFFSET : 0}
          y={y - TEXT_OFFSET}
          w={PAGE_WIDTH}
          size={size}
          align="center"
          style={{ color: FOOTER_TEXT, letterSpacing: `${spacing}pt` }}
        >
          {text}
        </At>
      ))}
    </>
  )
}

// Folha A4: cada folha é uma "página" na impressão
const Page = ({ last, children }) => (
  <div
    style={{
      position: 'relative',
      width: `${PAGE_WIDTH}pt`,
      height: `${PAGE_HEIGHT}pt`,
      overflow: 'hidden',
      background: '#fff',
      pageBreakAfter: last ? 'auto' : 'always',
      breakAfter: last ? 'auto' : 'page'
    }}
  >
    <div style={{ position: 'absolute', top: `${MODEL_SHIFT}pt`, left: 0, width: '100%' }}>
      <Sheet left={0}>
        <Block top={0} height={PAGE_HEIGHT} plain>
          {children}
        </Block>
      </Sheet>
    </div>
  </div>
)

const Cell = ({ width, background, border, children }) => (
  <div
    style={{
      boxSizing: 'border-box',
      width: width && `${width}pt`,
      flex: width ? undefined : 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      border,
      background,
      ...KEEP_COLORS
    }}
  >
    {children}
  </div>
)

const TableRow = ({ height, dark, cells }) => (
  <div style={{ display: 'flex', height: `${height}pt`, color: dark ? '#fff' : '#000' }}>
    {cells.map(([text, bold], index) => (
      <Cell
        key={index}
        width={index === 0 ? 190.3 : undefined}
        background={dark ? NAVY : '#fff'}
        border={`0.75pt solid ${dark ? GREEN : '#000'}`}
      >
        <span style={{ fontSize: '7pt', fontWeight: bold ? 'bold' : 'normal' }}>{text}</span>
      </Cell>
    ))}
  </div>
)

const SUMMARY = [
  ['VESSEL:', 'vessel', 139.7],
  ['GOODS:', 'goods', 166],
  ['TOTAL LOADED:', 'total', 190.4],
  ['PORT OF LOADING:', 'portOfLoading', 214.6],
  ['PORT OF DISCHARGE:', 'portOfDischarge', 239.4],
  ['DATE AND TIME OF START:', 'start', 266.1],
  ['DATE AND TIME OF END:', 'end', 290.4],
  ['LOCAL OF TREATMENT:', 'localOfTreatment', 317.9],
  ['METHOD:', 'method', 340],
  ['DOSAGE:', 'dosage', 361.4],
  ['TEMPERATURE:', 'temperature', 387.7],
  ['EXPOSURE TIME:', 'exposureTime', 416.5],
  ['HOLDS TREATED:', 'holdsTreated', 442.4]
]

const Cover = ({ data, last }) => {
  const rowHeight = data.shippers.length >= COMPACT_FROM ? COMPACT_ROW_HEIGHT : ROW_HEIGHT
  return (
    <Page last={last}>
      <Logo x={191.2} y={24.5} width={192.8} height={54.8} />
      <At x={146.4} y={104.3 - 1.8} w={300} size={17} bold align="center">
        PHOTOGRAPHIC REPORT
      </At>
      <div
        style={{
          position: 'absolute',
          boxSizing: 'border-box',
          left: '76.9pt',
          top: '136.2pt',
          width: '457.5pt',
          height: '335.3pt',
          border: LINE
        }}
      />
      {SUMMARY.map(([label, key, y]) => (
        <React.Fragment key={key}>
          <At x={79.2} y={y - TEXT_OFFSET} bold>
            {label}
          </At>
          <At x={252.4} y={y - TEXT_OFFSET}>
            {data[key]}
          </At>
        </React.Fragment>
      ))}
      <div style={{ position: 'absolute', left: '77pt', top: '472pt', width: '458.8pt' }}>
        <TableRow
          height={rowHeight}
          dark
          cells={[
            ['SHIPPER', true],
            ['QUANTITY', true]
          ]}
        />
        {data.shippers.map(({ name, quantity }, index) => (
          <TableRow key={index} height={rowHeight} cells={[[name], [quantity]]} />
        ))}
        <TableRow height={rowHeight} dark cells={[['TOTAL', true], [data.shippersTotal]]} />
        <div style={{ marginTop: '5.8pt', fontSize: '8.6pt', lineHeight: '11pt' }}>
          THE CARGO <span style={{ color: '#ff3131' }}>WAS NOT</span> CONDEMNED BY MOA.
        </div>
        <div style={{ marginTop: '12.7pt', fontSize: '8.6pt', lineHeight: '11pt' }}>
          PLEASE NOTE BELOW PHOTOS OF THE TREATMENT.
        </div>
      </div>
      <Footer cover />
    </Page>
  )
}

const CELL_WIDTH = 287.3
const CELL_HEIGHT = 215.4
const COLUMNS = [8.1, 299.9]
const ROWS = [108.3, 325.5, 542.7]

const PhotosPage = ({ photos, last }) => (
  <Page last={last}>
    <Logo x={195.7} y={22.9} width={204} height={57.8} />
    {photos.map((src, index) => (
      <img
        key={index}
        src={src}
        alt=""
        style={{
          position: 'absolute',
          left: `${COLUMNS[index % 2]}pt`,
          top: `${ROWS[Math.floor(index / 2)]}pt`,
          width: `${CELL_WIDTH}pt`,
          height: `${CELL_HEIGHT}pt`,
          objectFit: 'cover',
          objectPosition: '50% 34%'
        }}
      />
    ))}
    <Footer />
  </Page>
)

const PhotoReportDocument = ({ data }) => (
  <div id="pdfPage" style={{ width: `${PAGE_WIDTH}pt`, background: '#fff', fontFamily: FONT, color: '#000' }}>
    <style>{`@page { size: A4; margin: 0 }`}</style>
    <Cover data={data} last={!data.photoPages.length} />
    {data.photoPages.map((photos, index) => (
      <PhotosPage key={index} photos={photos} last={index === data.photoPages.length - 1} />
    ))}
  </div>
)

export default PhotoReportDocument
